import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { userHasRequiredRole } from '../firebase/hooks';
import { Roles } from '../firebase/interfaces';
import { pageIds } from 'utilities/constants';
import { useGetPage } from 'hooks/useGetPage';
import { AUTH_SIGN_UP_PROVIDER, DASHBOARD } from 'utilities/routes';
import { useAuth } from 'auth/context/AuthContext';
import { getFirebaseAuth } from '../firebase/config';

interface RoleRequiredRouteProps {
  allowedRoles: Roles[];
  children: ReactNode;
  notFullProviderDataRequired?: boolean;
}

const RoleRequiredRoute: React.FC<RoleRequiredRouteProps> = ({
  allowedRoles,
  children,
  notFullProviderDataRequired,
}) => {
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.ROLE_REQUIRED,
  });
  const navigate = useNavigate();
  const [isAllowed, setIsAllowed] = useState<boolean>(false);
  const hasBeenProcessed = useRef<boolean>(false);
  const { user, loadingUserFullInfo } = useAuth();
  const firebaseAuth = getFirebaseAuth();

  const checkRole = useCallback(async () => {
    const hasProviderRole = await userHasRequiredRole(
      Roles.PROVIDER,
      firebaseAuth,
    );

    for (const role of allowedRoles) {
      const isAllowed = await userHasRequiredRole(role, firebaseAuth);
      hasBeenProcessed.current = true;
      if (isAllowed) {
        setIsAllowed(true);
        break;
      }
    }

    if (
      allowedRoles.includes(Roles.PROVIDER) &&
      hasProviderRole &&
      !user?.displayName &&
      !notFullProviderDataRequired &&
      !user?.providerEnabled &&
      !loadingUserFullInfo
    ) {
      navigate(AUTH_SIGN_UP_PROVIDER, { replace: true });
    }
  }, [
    firebaseAuth,
    user,
    allowedRoles,
    notFullProviderDataRequired,
    loadingUserFullInfo,
    navigate,
  ]);

  useEffect(() => {
    checkRole();
  }, [checkRole]);

  if (loading || !locale || !hasBeenProcessed.current) return null;

  return isAllowed ? <>{children}</> : <Navigate to={DASHBOARD} />;
};

export default RoleRequiredRoute;
